import { lookupText, type LookupResult } from '../../lib/englishApi.js';
import type { WordLookupEntry, WordResolver } from './types.js';

/**
 * Resolver de la CONVERSACIÓN: pega a `/lookup` (read-through LRU → tabla global
 * → LLM) y cachea a nivel de módulo. Cumple la regla de costo del coach: el LLM
 * se llama una vez por palabra/frase nueva; las repeticiones (incluso en otra
 * burbuja) salen del cache, instantáneas y gratis.
 *
 * El cache vive a nivel de módulo a propósito — las traducciones de vocabulario
 * son estables, así que compartirlas en todo el page-load es deseable.
 */

// Cap: el cache vive todo el page-load; en una sesión SPA larga sin reload
// crecería monótono. El tope LRU acota la acumulación (evict del más viejo).
const CACHE_MAX = 500;
const cache = new Map<string, LookupResult>();

function key(text: string, context?: string): string {
  const t = text.toLowerCase().replace(/\s+/g, ' ').trim();
  return context ? `${t}@@${context.toLowerCase().trim()}` : t;
}

/** Devuelve el resultado cacheado si existe (lookup síncrono, sin red). */
export function peekLookup(text: string, context?: string): LookupResult | undefined {
  return cache.get(key(text, context));
}

/**
 * Resuelve un lookup: cache-hit instantáneo, o pega al backend y cachea.
 * `sessionId` viaja al backend (para cobrar + tracear el lookup) pero NO entra
 * en la cache key: la traducción de una palabra es la misma en cualquier sesión.
 */
export async function resolveLookup(
  text: string,
  context?: string,
  sessionId?: string,
): Promise<LookupResult> {
  const k = key(text, context);
  const hit = cache.get(k);
  if (hit) return hit;
  const result = await lookupText({
    text,
    ...(context ? { context } : {}),
    ...(sessionId ? { sessionId } : {}),
  });
  cache.set(k, result);
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return result;
}

/**
 * Construye el `WordResolver` de la conversación, con `sessionId` cerrado para
 * la atribución de costo del lookup. `LookupResult` es asignable a
 * `WordLookupEntry` (los campos ricos quedan ausentes → no se renderizan).
 */
export function makeConversationResolver(sessionId?: string): WordResolver {
  return {
    peekWord: (word, context): WordLookupEntry | undefined => peekLookup(word, context),
    resolveWord: (word, context): Promise<WordLookupEntry> =>
      resolveLookup(word, context, sessionId),
  };
}
